import {makeAutoObservable, observable} from 'mobx';
import {freshKey} from './PreorderCheckout';

const clone = value => JSON.parse(JSON.stringify(value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const uuid = value => typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const text = (value, cap) => typeof value === 'string' && value.trim() === value && value.length > 0 && value.length <= cap && !/[\u0000-\u001f\u007f-\u009f]/.test(value);
const exact = (value, keys) => value && typeof value === 'object' && !Array.isArray(value) &&
  Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const iso = value => typeof value === 'string' && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
const fail = () => {throw Error('Не удалось обновить доставку. Выберите пункт выдачи ещё раз.');};
const pointKeys = ['pointCode', 'name', 'address', 'latitude', 'longitude', 'workTime'];
function readCarrier(raw, command, selected, calculation) {
  const keys = ['schemaVersion', 'outcome', 'calculationId', 'deliveryId', 'expiresAt', 'dispatchEta', 'location', 'amountMinor', 'currency',
    'paymentChoices', ...(selected ? ['quoteId', 'point'] : ['points'])];
  if (!exact(raw, keys) || raw.schemaVersion !== `confirmed-preorder-cdek-${selected ? 'selection' : 'calculation'}-public-v1` ||
    raw.outcome !== 'quoted' || !uuid(raw.calculationId) || raw.calculationId !== command.calculationId || !id(raw.deliveryId) ||
    !iso(raw.expiresAt) || Date.parse(raw.expiresAt) <= Date.now() || !exact(raw.dispatchEta, ['from', 'to']) ||
    !iso(raw.dispatchEta.from) || !iso(raw.dispatchEta.to) || Date.parse(raw.dispatchEta.from) < Date.now() ||
    Date.parse(raw.dispatchEta.to) < Date.parse(raw.dispatchEta.from) ||
    !exact(raw.location, ['countryCode', 'fiasId', 'city']) || !/^[A-Z]{2}$/.test(raw.location.countryCode) ||
    !uuid(raw.location.fiasId) || !text(raw.location.city, 255) || raw.currency !== 'RUB' ||
    typeof raw.amountMinor !== 'string' || !/^(0|[1-9][0-9]{0,15})$/.test(raw.amountMinor) || !Number.isSafeInteger(Number(raw.amountMinor)) ||
    !Array.isArray(raw.paymentChoices) || !raw.paymentChoices.length || raw.paymentChoices.length > 64) fail();
  const paySeen = new Set();
  for (const payment of raw.paymentChoices) {
    if (!exact(payment, ['paymentMode', 'paymentMethodId']) || !['payment_now', 'payment_later'].includes(payment.paymentMode) || !id(payment.paymentMethodId)) fail();
    const key = JSON.stringify(payment); if (paySeen.has(key)) fail(); paySeen.add(key);
  }
  const points = selected ? [raw.point] : raw.points;
  if (!Array.isArray(points) || !points.length || points.length > 1000) fail();
  const seen = new Set();
  for (const point of points) {
    if (!exact(point, pointKeys) || !text(point.pointCode, 255) || !text(point.name, 255) || !text(point.address, 2048) ||
      !Number.isFinite(point.latitude) || Math.abs(point.latitude) > 90 || !Number.isFinite(point.longitude) || Math.abs(point.longitude) > 180 ||
      point.workTime !== null && !text(point.workTime, 2048) || seen.has(point.pointCode)) fail();
    seen.add(point.pointCode);
  }
  if (selected) {
    if (!uuid(raw.quoteId) || raw.point.pointCode !== command.pointCode || !calculation ||
      !['deliveryId', 'expiresAt', 'dispatchEta', 'location', 'amountMinor', 'paymentChoices'].every(key => same(raw[key], calculation[key])) ||
      !calculation.points.some(point => same(point, raw.point))) fail();
  } else if (raw.deliveryId !== command.deliveryId || !same(raw.location, command.location)) fail();
  return clone(raw);
}

// This small delivery context has no ordinary BasketStore methods or prices.
export default class PreorderDeliveryState {
  cityInfo = null; officePvz = null; selectedAddress = null; points = []; pending = false; error = '';
  calculation = null; deliveryId = null; revision = 0; disposed = false; timer = null;
  constructor({model, api, city = null, newKey = freshKey}) {
    this.model = model; this.api = api; this.newKey = newKey;
    const selectedCity = model.deliveryCity === undefined ? city : model.deliveryCity;
    this.cityInfo = selectedCity ? clone(selectedCity) : null;
    makeAutoObservable(this, {model: false, api: false, newKey: false, timer: false, cityInfo: observable.ref,
      calculation: observable.ref, points: observable.ref, officePvz: observable.ref}, {autoBind: true});
  }
  patch(value) {Object.assign(this, value);}
  setEditDataPickup() {} // Existing picker cleanup; no shared address store.
  dispose() {this.disposed = true; this.revision++; clearTimeout(this.timer);}
  clear() {
    if (this.model.locked) return false;
    this.revision++; clearTimeout(this.timer); this.model.clearCarrier();
    this.patch({officePvz: null, calculation: null, points: [], pending: false, error: ''}); return true;
  }
  setCityInfo(value) {
    if (this.disposed || !this.model.current() || !this.clear()) return;
    this.cityInfo = value ? clone(value) : null;
    this.model.rememberDeliveryCity(this.cityInfo);
  }
  snapshot() {return JSON.stringify(this.model.cart.captureSelection());}
  current(revision, snapshot) {return !this.disposed && this.model.current() && revision === this.revision && snapshot === this.snapshot() &&
    same(this.model.selection, this.model.cart.captureSelection());}
  lines() {return this.model.selection.lines.map(line => ({childProductId: line.identity.childProductId, colorId: line.identity.colorId,
    sizeId: line.identity.sizeId, quantity: line.quantity, categoryPath: null}));}
  uiPoint(point, data) {return {...point, id: point.pointCode, code: point.pointCode, type: 'sdek_pvz', pickupType: 'cdek',
    service: 'СДЭК', work_time: point.workTime, price: Number(data.amountMinor) / 100,
    deliveryNotice: 'Отправим после поступления товара.'};}
  async load(deliveryId) {
    if (!this.model.current() || this.model.locked || !same(this.model.selection, this.model.cart.captureSelection()) ||
      !this.model.carriers.some(row => row.deliveryId === deliveryId) || !this.clear()) return false;
    this.deliveryId = deliveryId;
    const revision = this.revision, snapshot = this.snapshot();
    try {
      const data = this.cityInfo?.data;
      if (!data) return false;
      const command = {calculationId: this.newKey(), deliveryId, lines: this.lines(),
        location: {countryCode: data.country_iso_code, fiasId: data.city_fias_id || data.fias_id, city: data.city || this.cityInfo.value}};
      this.pending = true;
      const value = readCarrier(await this.api.cdekCalculate(command), command, false);
      if (!this.current(revision, snapshot)) return false;
      this.patch({calculation: value, points: value.points.map(point => this.uiPoint(point, value))});
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {if (this.current(revision, snapshot) && !this.model.locked) {
        this.clear(); this.patch({error: 'Расчёт доставки устарел. Выберите пункт выдачи ещё раз.'});
      }}, Math.min(2147483647, Date.parse(value.expiresAt) - Date.now()));
      return true;
    } catch (_) {if (this.current(revision, snapshot)) this.patch({error: 'Не удалось обновить доставку. Выберите пункт выдачи ещё раз.'}); return false;}
    finally {if (this.current(revision, snapshot)) this.patch({pending: false});}
  }
  async selectPoint(point) {
    if (this.pending || this.model.locked || !this.model.current() || !same(this.model.selection, this.model.cart.captureSelection()) || !this.calculation ||
      Date.parse(this.calculation.expiresAt) <= Date.now() || !this.points.includes(point)) return false;
    const revision = this.revision, snapshot = this.snapshot(), calculation = this.calculation;
    this.model.clearCarrier(); this.patch({pending: true, officePvz: null, error: ''});
    try {
      const command = {quoteId: this.newKey(), calculationId: calculation.calculationId, pointCode: point.pointCode, lines: this.lines()};
      const value = readCarrier(await this.api.cdekSelect(command), command, true, calculation);
      if (!this.current(revision, snapshot) || !this.model.setCarrierSelection(value)) return false;
      this.patch({officePvz: this.uiPoint(value.point, value)}); return true;
    } catch (_) {if (this.current(revision, snapshot)) this.patch({error: 'Не удалось подтвердить пункт выдачи. Выберите его ещё раз.'}); return false;}
    finally {if (this.current(revision, snapshot)) this.patch({pending: false});}
  }
}
